import { KeyboardEventHandler, useEffect, useState } from 'react';

import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
// @ts-ignore
import { HiFilter } from 'react-icons/hi';
import { IoIosSearch } from 'react-icons/io';
import InfiniteScroll from 'react-infinite-scroller';
import { useEffectOnce, useWindowSize } from 'usehooks-ts';

import {
  countShopsByText,
  GetUserById,
  searchShopsByText,
} from '@/api/fetch/marketplace-api';
import {
  MatchParams,
  PaginationParams,
  SearchUsersByTitleRequest,
} from '@/api/gen-protos/discovery_pb';
import { UserByIDRequest } from '@/api/gen-protos/users_pb';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { LIMIT } from '@/lib/constants';
import { categories } from '@/lib/helpers';
import { useUsersPaginationHook } from '@/lib/hooks/useUsersPaginationHook';
import { cleanObject } from '@/lib/utils';
import { User } from '@/models/user';
import Layout from '@/modules/core/components/Layout';
import RecommendedShops from '@/modules/core/components/RecommendedShops';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import { IUserData } from '@/modules/core/types';
import InlistShop from '@/tayara-kit/banners/InListShop';
import Button from '@/tayara-kit/Button';
import CriteriaSelect from '@/tayara-kit/CriteriaSelect';
import Input from '@/tayara-kit/Input';
import PublisherCard from '@/tayara-kit/PublisherCard';
import Text from '@/tayara-kit/Text';
import { NEXT_API_URL } from 'environment';

// const PUBLISERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
// const OPTIONS: SelectOption[] = [
//   { label: 'Le plus cher', value: 'highest' },
//   { label: 'Le moin cher', value: 'lowest' },
//   { label: 'Le plus récent', value: 'most-recent' },
//   { label: 'Le plus pertinent', value: 'most-relevant' },

// ];

interface ShopsProps {
  shops: IUserData[];
  countShops: number;
}

const Shops = ({ shops, countShops }: ShopsProps) => {
  const [recommendedShopsIds, setRecommendedShopsIds] = useState('');
  const [agenceShopsIds, setAgenceShopsIds] = useState('');
  const [showRoomShopsIds, setShowRoomShopsIds] = useState('');
  const [multimediaShopsIds, setMultimediaShopsIds] = useState('');
  const [agenciesShopData, setAgenciesShopData] = useState<User[]>([]);
  const [showRoomsShopsData, setShowRoomsShopsData] = useState<User[]>([]);
  const [multimediaShopsData, setMultimediaShopsData] = useState<User[]>([]);

  const windowSize = useWindowSize();
  const GreenIndexes = [10, 20, 37];
  const {
    data: shopList,
    loadMoreShopsList,
    params,
    updateParamsState,
    page,
    selectedCategory,
    handleCategory,
    countData,
  } = useUsersPaginationHook({
    initialDataShopList: shops,
    id: '',
    countShops,
  });

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      loadMoreShopsList(true);
    }
  };

  const RecommendedShopsTitles = {
    agenciesShopsTitle: 'Agences recommandées',
    showRoomShopsTitle: 'Showrooms recommandés',
    multimediaShopsTitle: 'Shops recommandés',
  };

  const getUserData = async (kcid: string) => {
    const req = new UserByIDRequest();
    req.setKcid(kcid);
    const userData = await GetUserById(req);
    const userFormat: User = {
      avatar: userData.getUser()?.getCurrentstate()?.getAvatar(),
      kcid: userData?.getUser()?.getKcid(),
      productType: userData?.getUser()?.getProducttype(),
      userType: userData?.getUser()?.getUsertype(),
      phonenumber: userData.getUser()?.getCurrentstate()?.getPhonenumber(),
      shopBackground: userData.getUser()?.getShopbackgroundimage(),
      fullname: userData.getUser()?.getCurrentstate()?.getFullname(),
      email: userData.getUser()?.getCurrentstate()?.getEmail(),
    };

    return userFormat;
  };

  useEffectOnce(() => {
    rudderstackTrackEvent('page_view', { page_type: 'shops' });

    fetch(`${NEXT_API_URL}/settings`)
      .then((res) => res.json())
      .then((settings) => {
        if (settings?.recommendedShops !== undefined) {
          const agences = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'agence'
          );
          const showRooms = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'showRoom'
          );
          const multimediaShops = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'multimedia'
          );
          setAgenceShopsIds(agences.map((shop: any) => shop.recommendedShopId));
          setShowRoomShopsIds(
            showRooms.map((shop: any) => shop.recommendedShopId)
          );
          setMultimediaShopsIds(
            multimediaShops.map((shop: any) => shop.recommendedShopId)
          );
          setRecommendedShopsIds(
            settings?.recommendedShops.map(
              (recommendedShop: any) => recommendedShop.recommendedShopId
            )
          );
        }
      });
  });

  useEffect(() => {
    if (Array.isArray(agenceShopsIds) && agenceShopsIds.length > 0) {
      const fetchShopData = async () => {
        const promises = agenceShopsIds.map((id: string) => getUserData(id));
        const userData = await Promise.all(promises);
        setAgenciesShopData(userData);
      };
      fetchShopData();
    }
    if (Array.isArray(showRoomShopsIds) && showRoomShopsIds.length > 0) {
      const fetchShopData = async () => {
        const promises = showRoomShopsIds.map((id: string) => getUserData(id));
        const userData = await Promise.all(promises);
        setShowRoomsShopsData(userData);
      };
      fetchShopData();
    }
    if (Array.isArray(multimediaShopsIds) && multimediaShopsIds.length > 0) {
      const fetchShopData = async () => {
        const promises = multimediaShopsIds.map((id: string) =>
          getUserData(id)
        );
        const userData = await Promise.all(promises);
        setMultimediaShopsData(userData);
      };
      fetchShopData();
    }
  }, [
    recommendedShopsIds,
    agenceShopsIds,
    showRoomShopsIds,
    multimediaShopsIds,
  ]);

  return (
    <>
      {/* Results header and sorting */}
      <div className="flex flex-col md:flex-row justify-between gap-3 items-center w-full mb-10 mt-4 lg:mt-0">
        {/* West-side (on the left) */}

        <div className="flex w-full flex-col">
          <Text
            text={
              selectedCategory?.id === '66c34239d16d7f3b0c2ec56e'
                ? 'Visiter un projet'
                : 'Visiter une boutique'
            }
            as="h1"
            className="text-2xl font-bold text-gray-700 text-center lg:text-start"
          />
          <Text
            text={`${countData} ${
              selectedCategory?.id === '66c34239d16d7f3b0c2ec56e'
                ? ' projets'
                : ' boutique'
            } `}
            as="data"
            className="block text-sm lg:text-base font-bold text-info text-center lg:text-start"
          />
        </div>

        {/* West-side (on the right) */}
        <div className="flex justify-end gap-3 flex-col  lg:flex-row w-full">
          {/* *** *** *** */}
          {/* To be uncommented later when search and sorting are properly implemented */}
          {/* Shops search input */}
          <Input
            startIcon={IoIosSearch}
            iconSize={18}
            placeholder={
              selectedCategory?.id === '66c34239d16d7f3b0c2ec56e'
                ? 'Trouvez un projet...'
                : 'Trouvez une boutique...'
            }
            value={params?.title}
            onChange={(e) => updateParamsState('title', e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div>
            <CriteriaSelect
              onChange={handleCategory}
              value={selectedCategory.id}
              displayLabel="Catégorie"
              options={categories
                .filter((cat) => cat.parentcategoryxid === '')
                .map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
            ></CriteriaSelect>
          </div>
          <Button
            aria-label="Filtrer"
            className="btn-primary"
            icon={<HiFilter size={20} className=" text-white mr-2" />}
            onClick={() => {
              loadMoreShopsList(true);
            }}
          >
            <span className="tracking-widest uppercase">Filtrer</span>
          </Button>

          {/* <Select
          className="rounded-full select-sm w-[160px] max-w-full"
          displayLabel="Trier par"
          options={OPTIONS}
          onChange={(e) => setSort(e.target.value)}
        /> */}
        </div>
      </div>
      {selectedCategory.name === 'Immobilier' &&
        agenciesShopData.length > 0 && (
          <RecommendedShops
            showRoomsShopsData={agenciesShopData}
            title={RecommendedShopsTitles.agenciesShopsTitle}
          />
        )}
      {selectedCategory.name === 'Véhicules' &&
        showRoomsShopsData.length > 0 && (
          <RecommendedShops
            showRoomsShopsData={showRoomsShopsData}
            title={RecommendedShopsTitles.showRoomShopsTitle}
          />
        )}
      {selectedCategory.name === 'Informatique et Multimedias' &&
        multimediaShopsData.length > 0 && (
          <RecommendedShops
            showRoomsShopsData={multimediaShopsData}
            title={RecommendedShopsTitles.multimediaShopsTitle}
          />
        )}
      {/* Publisher list  */}
      <InfiniteScroll
        pageStart={page}
        threshold={3000}
        loadMore={() => loadMoreShopsList(false)}
        initialLoad={false}
        hasMore={true}
      >
        <div
          className="
    grid
    grid-cols-1       
    md:grid-cols-2    
    lg:grid-cols-3
    xl:grid-cols-4    
    gap-8             
    mt-4
  "
        >
          {shopList.map((publisher, i) =>
            GreenIndexes.includes(i) && windowSize.width > 1024 ? (
              <InlistShop key={i} />
            ) : (
              <PublisherCard
                key={i}
                userDetails={publisher}
                NeufPage={publisher?.categoryId === '66c34239d16d7f3b0c2ec56e'}
              />
            )
          )}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default Shops;

// Dynamic layout configuration
Shops.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Layout.Wide>{page}</Layout.Wide>
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
}): Promise<GetServerSidePropsResult<ShopsProps>> => {
  const searchShopRequest = new SearchUsersByTitleRequest();
  const paginationParams = new PaginationParams();

  paginationParams.setLimit(LIMIT);
  paginationParams.setSkip(0);

  const matchParams = new MatchParams();
  matchParams.setPaginationparams(paginationParams);
  searchShopRequest.setMatchparams(matchParams);

  const [shops, countShops] = await Promise.all([
    searchShopsByText('', searchShopRequest),
    countShopsByText(searchShopRequest),
  ]);
  const mapppedShops = shops.map((shop) => mapUserResponseToIUserData(shop));

  return {
    props: cleanObject({
      ...(await serverSideTranslations(locale || 'en')),
      shops: mapppedShops,
      countShops: countShops.getCount(),
    }),
  };
};
